import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable ,  ReplaySubject,  Subject } from 'rxjs';
import { ExceptionService, PaginatedList, UtilsService } from '../../../xform-compat';
import { RmsBasicCrudService } from '../../components/rms-basic-crud.service';
import { Note } from '../../../shared/models';
import { ContentService } from '../../services';

/**
 * Where a note lives, resolved from the note id alone so a note deep-link
 * (?noteId=) can open the correct space and page without the caller knowing
 * either. `found` is false when the note id cannot be resolved to a viewable
 * location (deleted, inactive, or a CRM note the user cannot read).
 *
 * `noteId` is the globally-unique text_content id carried by the deep-link URL;
 * `noteContainerId` is the per-container primary key the notes list renders and
 * anchors on, used internally to page to and scroll to the note.
 */
export interface NoteLocation {
  noteId: number;
  noteContainerId: number;
  spaceId: number;
  companyId: number;
  page: number;
  found: boolean;
}

/**
 * Injectable service for company notes
 */
@Injectable({
  providedIn: 'root'
})
export class CompanyNotesService extends RmsBasicCrudService<any> {
  private companyNoteCopied: Subject<any>;
  private companyNotesUpdate: Subject<any>;
  private showCompleteNote: ReplaySubject<boolean | null>;

  constructor(
    http: HttpClient,
    utilsService: UtilsService,
    exceptionService: ExceptionService,
    private contentService: ContentService
  ) {
    super(http, utilsService, exceptionService);
    this.companyNotesUpdate = new Subject<any>();
    this.companyNoteCopied = new Subject<any>();
    this.showCompleteNote = new ReplaySubject<boolean | null>(1);
  }

  /**
   * Get notes per space id
   * @param params
   */
  public getSpaceNotes (params?: any): Observable<PaginatedList<Note>> {
    return this.contentService.formatInlineImageObservablePaginatedNote(super.getData(`/api/rms/combinedcompanynotes/notes`, params));
  }

    /**
   * Get note per space id
   * @param spaceId
   * @param params
   */
    public getSpaceNote (spaceId: number, noteId: number): Observable<Note> {
      return super.getData(`/api/rms/spaces/${spaceId}/notes/${noteId}`);
    }

  /**
   * Create a new company note based on the current selected space
   * @param spaceId
   * @param body
   */
  public createSpaceNote (spaceId: number, body: {
    content: string,
    attachmentIds: Array<number>,
    isOverview: boolean
  }): Observable<Note> {
    body.content = this.contentService.formatInlineImageContentRemove(body.content);
    return super.saveData(`/api/rms/spaces/${spaceId}/notes`, body);
  }

  /**
   * Creates a new note draft
   * @param spaceId
   * @param body
   */
  public createNoteDraft (spaceId: number, body: {content: string, attachmentIds: Array<number>, isOverview: boolean}): Observable<Note> {
    body.content = this.contentService.formatInlineImageContentRemove(body.content);
    return super.saveData(`/api/rms/spaces/${spaceId}/notes/draft`, body);
  }

  /**
   * Autocompletes the notes authors to filter notes
   * @param spaceId
   * @param params
   */
  public getAuthors (params?: any): Observable<PaginatedList<{userId: number, displayName: string}>> {
    return super.getData(`/api/rms/combinedcompanynotes/notes/authors`, params);
  }

  /**
   * Search notes by search term
   * @param spaceId
   * @param body
   */
  public searchNotes (body: any, queryParams?: string): Observable<PaginatedList<Note>> {
    return super.saveData(`/api/rms/combinedcompanynotes/notes/search${queryParams}`, body);
  }

  /**
   * Toggle note overview
   * @param noteId
   */
    public toggleNoteOverview (noteId: number): Observable<Note> {
      return super.updateData(`/api/rms/combinedcompanynotes/toggle/overview/${noteId}`, {});
    }

  /**
   * transfer crm note
   * @param noteId
   * @param crmId - the crm item id to transfer to
   */
    public transferCrmNoteToAnother (noteId: number, crmId: number): Observable<void> {
      return super.updateData(`/api/rms/combinedcompanynotes/transfer/${noteId}/to/${crmId}`, {});
    }

  /**
   * Gets the history for a note
   * @param spaceId
   * @param noteId
   * @param params
   */
  public getNoteHistory (spaceId: number, noteId: number, params?: any): Observable<Array<Note>> {
    return this.contentService.formatInlineImageObservableNoteArray(
      super.getData(
        `/api/rms/spaces/${spaceId}/notes/${noteId}/history?extrafield=textContentCompliance&extrafield=textContentCompanyFinancialSnapshot&sort=created&direction=DESC`, params)
    );
  }

  /**
   * Gets the history for a note
   * @param indicationId
   * @param noteId
   * @param params
   */
  public getIndicationHistory (indicationId: number, noteId: number, params?: any): Observable<Array<Note>> {
    return this.contentService.formatInlineImageObservableNoteArray(
      super.getData(
        `/api/rms/indications/` +
        `${indicationId}/notes/${noteId}/history?extrafield=textContentCompliance&extrafield=textContentCompanyFinancialSnapshot`,
        params
      )
    );
  }

/**
   * Gets the history for a note
   * @param noteId
   * @param params
   */
  public getInsightHistory (noteId: number, params?: any): Observable<Array<Note>> {
    return this.contentService.formatInlineImageObservableNoteArray(
      super.getData(`/api/rms/insights/${noteId}/history?extrafield=textContentCompliance&extrafield=textContentCompanyFinancialSnapshot`, params));
  }

  /**
   * Returns the page of the most recent Overview Note
   * @param spaceId
   * @param params
   */
  public getMostRecentOverviewNote(params?: any): Observable<any> {
    return super.getData(`/api/rms/combinedcompanynotes/notes/overview`, params);
  }

  /**
   * Edit and existing note and save a draft
   * @param spaceId
   * @param noteId
   * @param body
   */
  public saveDraftForPosted (
    spaceId: number,
    noteId: number,
    body: {content: string, attachmentIds: Array<number>, isOverview: boolean}
    ): Observable<Note> {
    body.content = this.contentService.formatInlineImageContentRemove(body.content);
    return super.saveData(`/api/rms/spaces/${spaceId}/notes/${noteId}/draft`, body);
  }

  /**
   * Post a new version of the note
   * @param spaceId
   * @param noteId
   * @param body
   */
  public updateNote (spaceId: number, noteId: number, body: any): Observable<Note> {
    body.content = this.contentService.formatInlineImageContentRemove(body.content);
    return super.updateData(`/api/rms/spaces/${spaceId}/notes/${noteId}`, body);
  }

  /**
   * Saves note review by compliance
   * @param spaceId
   * @param noteId
   * @param body
   */
  public reviewComplianceNote (spaceId: number, noteId: number, body: any): Observable<null> {
    return super.saveData(`/api/rms/spaces/${spaceId}/notes/${noteId}/review`, body);
  }

  /**
   * Updates note object references
   * @param spaceId
   * @param noteId
   * @param body
   */
  public updateNoteReferences (spaceId: number, noteId: number, body: any): Observable<Note> {
    return super.updateData(`/api/rms/spaces/${spaceId}/notes/${noteId}/references`, body);
  }

  /**
   * Get note object references
   * @param spaceId
   * @param noteId
   */
  public getNoteReferences (spaceId: number, noteId: number): Observable<any> {
    return super.getData(`/api/rms/spaces/${spaceId}/notes/${noteId}/references`);
  }

  /**
   * Get user note drafts
   * @param params
   */
  public getNotesDrafts (params?: any): Observable<PaginatedList<Note>> {
    return this.contentService.formatInlineImageObservablePaginatedNote(super.getData(`/api/rms/combinedcompanynotes/notes/draft/me`, params));
  }

  /**
   * Gets a single note
   * @param spaceId
   * @param noteId
   */
  public getSingleNote (spaceId: number, noteId: number): Observable<Note> {
    return this.contentService.formatInlineImageObservableNote(super.getData(`/api/rms/spaces/${spaceId}/notes/${noteId}`));
  }

  /**
   * Resolves the space (and page) a note lives on from the note id alone, so a
   * note deep-link (?noteId=) can open the notes section on the correct space
   * without the caller knowing the space. Company notes resolve to their own
   * space; CRM/Sonar notes resolve to the company's default space.
   * @param noteId the deep-linked note id, either a text content id or a per-container note id
   * @param crmIds the company's CRM item ids, used to compute the exact page for a company note
   * @param pageSize the page size the notes list renders with
   * @param companyId the company whose page the link was opened on; disambiguates container ids,
   *                  which are only unique within their own container table
   */
  public resolveNoteLocation (noteId: number, crmIds: Array<number> = [], pageSize?: number,
                              companyId?: number): Observable<NoteLocation> {
    const params: any = { crmId: crmIds };
    if (pageSize) {
      params.pagesize = pageSize;
    }
    if (companyId) {
      params.companyId = companyId;
    }
    return super.getData(`/api/rms/combinedcompanynotes/notes/${noteId}/location`, params);
  }

  /**
   * Discard draft note
   * @param spaceId
   * @param noteId
   */
  public discardNoteDraft (spaceId: number, noteId: number): Observable<null> {
    return super.deleteData(`/api/rms/spaces/${spaceId}/notes/${noteId}/draft/me`);
  }

  /**
   * Sends a message to the subscribed object on the component
   * @param message
   */
  public sendMessage(message: string) {
    this.companyNotesUpdate.next({ text: message });
  }

  /**
   * Observable to listen when a message is sent
   */
  public getMessage(): Observable<any> {
    return this.companyNotesUpdate.asObservable();
  }

  /**
   * Sends a message to the subscribed object on the component
   * @param note
   */
  public sendCopiedNote(note: any) {
    this.companyNoteCopied.next(note);
  }

  /**
   * Observable to listen when a message is sent
   */
  public getCopiedNote(): Observable<any> {
    return this.companyNoteCopied.asObservable();
  }

  /**
   * Set the showCompleteNote value
   */
  public setShowCompleteNote(show: boolean | null): void  {
    this.showCompleteNote.next(show);
  }

  /**
   * Observable to listen the showCompleteNote status change
   */
  public getShowCompleteNote(): Observable<boolean | null> {
    return this.showCompleteNote.asObservable();
  }
}
